<template>
  <div>
    <div class="row mt-3 mb-3">
      <div class="col-12">
        <section-title
          icon="fas fa-id-card"
          content="Servicios Terapia"
          container-class="tag-badge-main-info mt-1 mb-1"
        />
      </div>
    </div>
    <Error
      v-if="showRequiredAlert"
      class="mt-3 pl-3"
      is-alert-mode
      message="Esta sección no es requerida para pasar el PEI a Completado, pero al momento de Completar la Hoja de Cotejo debe estar completada. En el caso de que la misma no esté documentada deben pasar el PEI a Borrador para completarla y poder llevar a Firmado el PEI."
    />
    <Error
      v-if="hasCompensatoryHours"
      class="mt-3 pl-3"
      is-alert-mode
      message="El estudiante no cuenta con planillas de compensación de servicio."
    />
    <div v-if="!hasCompensatoryHours">
      <!-- CATEGORY 1 (Therapy) -->
      <service-compensation-tab-service
        v-for="(groupedItem, i) in compensatoryHourRequestsGroupedTherapy"
        :key="`therapy-${groupedItem.programServiceAreaId}-${i}`"
        :service="groupedItem"
        :program-service-area-id="groupedItem.programServiceAreaId"
        :compensatory-hour-requests="groupedItem.items"
        :sie="sie"
        :pei-id="peiId"
        :is-pei-complete="isPeiComplete"
        :can-show-validation="canShowValidation"
        :read-only="readOnly"
        class="col-12"
        @load-data="loadData"
      />
    </div>
    <!-- ================== CATEGORY 2 (RP) ================== -->
    <div v-if="compensatoryHourRequestsGroupedRp.length">
      <div class="row mt-4 mb-3">
        <div class="col-12">
          <section-title
            icon="fas fa-id-card"
            content="Servicios Terapia RP"
            container-class="tag-badge-main-info mt-1 mb-1"
          />
        </div>
      </div>

      <service-compensation-tab-service-rp
        v-for="(groupedItem, i) in compensatoryHourRequestsGroupedRp"
        :key="`rp-${groupedItem.programServiceAreaId}-${i}`"
        :service="groupedItem"
        :program-service-area-id="groupedItem.programServiceAreaId"
        :compensatory-hour-requests="groupedItem.items"
        :sie="sie"
        :pei-id="peiId"
        :is-pei-complete="isPeiComplete"
        :can-show-validation="canShowValidation"
        :read-only="readOnly"
        class="col-12"
        @load-data="loadData"
      />
    </div>
  </div>
</template>

<script>
import Error from '@/components/common/Error';
import { getInterventions } from '@/modules/compensatory-service/api';
import mixAlert from '@/mixin/alert.mixin';
import { SectionTitle } from '@/components/pei/shared';

import ServiceCompensationTabService from './ServiceCompensationTabService.vue';
import ServiceCompensationTabServiceRp from './ServiceCompensationTabServiceRp.vue';
import compensatoryRequestCategories from '@/utils/constants/compensatoryRequestCategory.js';

export default {
  name: 'ServiceCompensationTabRow',
  mixins: [mixAlert],
  components: {
    Error,
    ServiceCompensationTabService,
    ServiceCompensationTabServiceRp,
    SectionTitle,
  },
  data() {
    return {
      compensatoryHourRequests: [],
      compensatoryHourRequestsGroupedTherapy: [],
      compensatoryHourRequestsGroupedRp: [],
      interventionToSave: [],
      hasData: [],
      requestNumberList: [],
      validate: false,
      element: null,
      isModify: false,
      pendingElementsIntervention: [],
      originalCompensatoryHourRequests: [],
    };
  },
  computed: {
    hasCompensatoryHours() {
      return this.compensatoryHourRequests?.length === 0;
    },
    showRequiredAlert() {
      if (this.readOnly && this.hasCompensatoryHours) return false;
      if (!this.readOnly) return true;
      return true;
    },
  },
  props: {
    sie: {
      default: 0,
    },
    peiId: {
      type: String,
      default: '',
    },
    disciplineTypeId: {
      default: 0,
    },
    programServiceAreaId: {
      default: 0,
    },
    isPeiComplete: {
      default: false,
    },
    canShowValidation: {
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    activeEducationalCompensatoryService: {
      type: Boolean,
      default: false,
    },
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    async loadData() {
      const { data } = await getInterventions(
        this.sie,
        this.disciplineTypeId,
        this.peiId,
        { includeRP: true }
      );
      this.requestNumberList = [];

      this.compensatoryHourRequests = data.compensatoryHourRequests.map(
        (item) => {
          this.requestNumberList.push(item.requestNumber);
          return {
            ...item,
            isModify: item.preSaveInterventionAmount != null ? true : false,
            isOpen:
              this.readOnly &&
              (item.documentId != null ||
                item.preSaveInterventionAmount != null)
                ? true
                : false,
          };
        }
      );

      // Crear un listado nuevo agrupado por disciplineName
      const therapy = this.compensatoryHourRequests.filter(
        (x) => x.category === compensatoryRequestCategories.Thrpy
      );
      const rp = this.compensatoryHourRequests.filter(
        (x) => x.category === compensatoryRequestCategories.RP
      );

      // ===== AGRUPAR THERAPY POR disciplineName =====
      const groupedTherapy = {};
      therapy.forEach((item) => {
        const key = item.detail.disciplineName;
        if (!groupedTherapy[key]) {
          groupedTherapy[key] = [];
        }
        groupedTherapy[key].push(item);
      });

      this.compensatoryHourRequestsGroupedTherapy = Object.keys(
        groupedTherapy
      ).map((name) => ({
        disciplineName: name,
        programServiceAreaId:
          groupedTherapy[name][0].detail.programServiceAreaId,
        items: groupedTherapy[name],
      }));

      // ===== AGRUPAR RP TAMBIÉN POR disciplineName =====
      const groupedRp = {};
      rp.forEach((item) => {
        const key = item.detail.disciplineName;
        if (!groupedRp[key]) {
          groupedRp[key] = [];
        }
        groupedRp[key].push(item);
      });

      this.compensatoryHourRequestsGroupedRp = Object.keys(groupedRp).map(
        (name) => ({
          disciplineName: name,
          programServiceAreaId: groupedRp[name][0].detail.programServiceAreaId,
          items: groupedRp[name],
        })
      );
    },
  },
};
</script>

<style scoped></style>
